import { ClipboardService } from '@utils/copy-to-clipboard-util';
import { ModalsService } from '@services/modals.service';
import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PaymentStore } from '../../store/payment-report.store';
import { MatIcon } from '@angular/material/icon';
import { AssetStore } from '@stores/asset.store';
import { PaymentService } from '@services/payment.service';
import { tap, catchError, EMPTY, firstValueFrom, lastValueFrom } from 'rxjs';
import RoundUpUtil from '@utils/round-up-utils';
import { minutes } from '@environments/environment';
import { CommonModule } from '@angular/common';
import { TransactionIdInputModalComponent } from '@components/modals/transaction-id-input-modal/transaction-id-input-modal.component';
import { CreatePaymentReportDto } from '@dtos/create-payment-report.dto';
import { EIP6963ProviderDetail } from '@app/types/eip-6369';
import { ToastrFactoryService } from '@services/toastr-factory.service';
import { NickyButtonComponent } from '@components/form/nicky-button/nicky-button.component';

import { PaymentUtils } from '../../utils/payment-utils';
import { ScanQrModalComponent } from '@components/modals/scan-qr-modal/scan-qr-modal.component';
import { MatTooltip } from '@angular/material/tooltip';
import { GetTxInfoResult, TokenTransfer } from 'src/app/components/models/get-tx-info-result.model';
import { ConfirmPaymentReportWithErrorModalComponent } from 'src/app/components/modals/confirm-payment-report-with-error-modal/confirm-payment-report-with-error-modal.component';
import { QuoteModel } from '@models/quote.model';
import { toggleMatTooltipDirective } from '@directives/toggleMatTooltip.directive';
import { CurrencyTranslationPipe } from '@pipes/currency-translation.pipe';
import { QrCodeHandlerComponentComponent } from '@components/qr-code-handler-component/qr-code-handler-component.component';
import { formatCryptoAddressOrTx } from '@utils/format-eth-matic-address.utils';
import { isManual } from '@utils/crypto-data-souce.utils';
import { formatNetworkName } from '@utils/network-utils';
import { AuthStore } from '@stores/auth.store';
import { WalletBrowserExtensionStore } from '@stores/browser-wallet.store';
import { ResumePayment } from '@app/components/models/resume-payment.model';
import { WizardReportStore } from '../../store/wizard-report.store';
import { NickyUserGuestCardComponent } from '../../../../../../components/nicky-user-guest-card/nicky-user-guest-card.component';

interface TransactionChecks {
  wasSentToExpectedReceiver: boolean;
  correctAsset: boolean;
  usedAsset?: string | null | undefined;
  expectedAsset?: string | null | undefined;
}

@Component({
  selector: 'app-confirmation-step',
  imports: [
    TranslateModule,
    MatIcon,
    CommonModule,
    NickyButtonComponent,
    MatTooltip,
    toggleMatTooltipDirective,
    CurrencyTranslationPipe,
    QrCodeHandlerComponentComponent,
    NickyUserGuestCardComponent,
  ],
  templateUrl: './confirmation-step.component.html',
})
export class ConfirmationStepComponent implements OnInit, OnDestroy {
  protected wizardReportStore = inject(WizardReportStore);
  protected paymentStore = inject(PaymentStore);
  protected authStore = inject(AuthStore);
  protected paymentService = inject(PaymentService);
  protected modalsService = inject(ModalsService);
  protected roundUpUtil = inject(RoundUpUtil);
  protected toastrFactory = inject(ToastrFactoryService);
  protected translate = inject(TranslateService);
  protected assetStore = inject(AssetStore);
  protected paymentUtils = inject(PaymentUtils);
  protected clipboardService = inject(ClipboardService);
  protected walletStore = inject(WalletBrowserExtensionStore);

  protected copyToClipboard = (data: string | number, customText?: string) =>
    this.clipboardService.copyToClipboard(data, customText);

  public flippedRates: boolean = false;

  public totalTimeInMinutes: number = minutes;
  protected timerPercentage: number = 0;
  public refreshQuotesTimerInSeconds: number = 15 * 60;
  private getCurrencyInterval: any;
  public timer!: any;
  protected invalidQuote = !this.paymentStore.paymentDetailsComputed().rates?.price;
  public endtime!: Date;
  public awaitingForWalletExtension = signal(false);
  public qrCodeError = signal(false);

  private successMetamaskTransaction = false;

  public selectedWallet!: EIP6963ProviderDetail;
  public availableWallets = this.walletStore.browserWalletsObject();

  public formatedAssetName = signal(
    this.paymentStore.paymentDetailsComputed().payingAsset?.assetName?.replace(/\s*\([^)]*\)/g, ''),
  );

  formatPrice(value: string | number, payingAsset?: boolean) {
    return this.paymentUtils.formatPrice(value, payingAsset);
  }

  getNetworkName() {
    return this.paymentUtils.getNetworkName(this.paymentStore.paymentDetailsComputed().payingAsset?.assetChain || '');
  }

  async openQrScanModal() {
    await this.modalsService.openModal(ScanQrModalComponent, {});
  }

  getWalletExchange(onlyWalletInfo?: boolean) {
    const walletProviderName =
      this.paymentStore.paymentDetailsComputed().selectedReceiverWallet?.cryptoAddress?.cryptoDataSource;
    const termsPhrasePropertys = {
      walletProviderName: this.translate.instant(walletProviderName || ''),
      assetName: this.paymentStore.paymentDetailsComputed().payingAsset?.assetName,
    };

    const phrase = isManual(walletProviderName)
      ? this.translate.instant('privateWalletProviderInform', termsPhrasePropertys)
      : this.translate.instant('exchangeWalletProviderInform', termsPhrasePropertys);

    return onlyWalletInfo ? phrase : `${phrase} ${this.translate.instant('nickyIsAPeerToPeer')}`;
  }

  getAddressViaNetwork() {
    const addresViaNetworkPropertys = {
      payingAsset: this.paymentStore.paymentDetailsComputed().payingAsset?.assetTicker,
      currentNetwork: this.translate.instant(this.getNetworkName().toLowerCase() + 'Network'),
    };
    return this.translate.instant('payingAssetViaNetwork', addresViaNetworkPropertys);
  }

  nextStep() {
    this.clearTimer();
  }

  setQuoteInfo(quote: QuoteModel | undefined) {
    if (!quote) return;

    const sameAssets = quote.from.id === quote.to.id;
    this.invalidQuote = false;
    const validEndTimeDate = new Date(quote.validEndTime);

    if (!quote.price && !sameAssets) {
      this.invalidQuote = true;
    }

    if (sameAssets) {
      quote.price = this.paymentStore.paymentDetailsComputed().settlementAmount;
    }

    if (!this.paymentStore.paymentDetailsComputed().shouldKeepQuote) {
      this.checkIfQuoteIsValid(validEndTimeDate);
    }

    this.endtime = validEndTimeDate;

    quote.price = this.roundUpUtil.roundUpAmount(
      quote.price,
      this.assetStore.decimalPlacesFor()[this.paymentStore.paymentDetailsComputed().payingAsset?.assetTicker || 'USD'],
    );

    this.paymentStore.updatePaymentDetails({ quote: quote });

    if (!this.paymentStore.paymentDetailsComputed().shouldKeepQuote) {
      this.startTimer();
    }

    if (!this.paymentStore.resumePayment()) {
      this.sendResumePaymentEmail();
    }

    // Update URL with new quote information
    this.paymentStore.generateReturnLink();
  }

  async sendResumePaymentEmail() {
    const resumePayment: ResumePayment = {
      ...this.paymentStore.paymentDetailsComputed(),
      email: this.authStore.profile()?.email || this.paymentStore.paymentDetailsComputed().email,
      quoteId: this.paymentStore.paymentDetailsComputed().quote?.id || '',
      name: this.authStore.profile()?.publicName || this.paymentStore.paymentDetailsComputed().payerName,
      paymentId: this.paymentStore.paymentRequest?.()?.bill.shortId || '',
      receiverId: this.paymentStore.targetUser?.()?.shortId || '',
      cultureInfo: this.translate.currentLang,
    };

    try {
      await firstValueFrom(this.paymentService.sendResumePaymentEmail(resumePayment));
      this.toastrFactory.success(this.translate.instant('PAYMENT_REPORT.emailNotificationSent'));
    } catch (e) {
      this.toastrFactory.error(this.translate.instant('somethingWentWrongResumePaymentEmail'));
      console.log(e);
    }
  }

  getQuotes() {
    if (this.paymentStore.resumePayment()) {
      this.setQuoteInfo(this.paymentStore.paymentDetailsComputed().quote);
      this.startTimer();
      return;
    }

    const { payingAsset, settlementAsset, settlementAmount } = this.paymentStore.paymentDetailsComputed();
    const formatedSettlementAsset = settlementAsset?.isMultiChain
      ? `ETH.${settlementAsset.assetTicker}`
      : settlementAsset?.id || '';

    const quoteInfo = {
      paymentValue: settlementAmount || 0,
      payingAsset: formatedSettlementAsset,
      selectedAsset: payingAsset?.id || '',
    };

    this.paymentService
      .getQuotes(quoteInfo.paymentValue, quoteInfo.payingAsset, quoteInfo.selectedAsset)
      .pipe(
        tap((response: any) => {
          this.setQuoteInfo(response);
        }),
        catchError((error: any) => {
          this.invalidQuote = true;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  clearTimer() {
    clearInterval(this.timer);
    clearInterval(this.getCurrencyInterval);
  }

  sameAssets() {
    const payingAsset = this.paymentStore.paymentDetailsComputed().payingAsset?.assetTicker;
    const settlementAsset = this.paymentStore.paymentDetailsComputed().settlementAsset?.assetTicker;
    return payingAsset === settlementAsset;
  }

  checkIfQuoteIsValid(validEndTimeDate: Date) {
    const userTime = new Date();
    if (validEndTimeDate > userTime) {
      const timeDifference = validEndTimeDate.getTime() - userTime.getTime();
      const minutesDifference = timeDifference / (1000 * 60);

      this.totalTimeInMinutes = Math.round(minutesDifference);
      this.refreshQuotesTimerInSeconds = minutesDifference * 60;
      return;
    }

    this.wizardReportStore.setStep('outdated');
  }
  async checkIfItsTheExpectedReceiver(getTxInfoResult: GetTxInfoResult[]): Promise<boolean> {
    const tokenTransfers = getTxInfoResult?.[0]?.txRawData?.tokenTransfers;
    const expectedAddress = this.paymentStore
      .paymentDetailsComputed()
      ?.selectedReceiverWallet?.cryptoAddress?.address?.toLowerCase();
    const expectedAsset = this.paymentStore.paymentDetailsComputed()?.payingAsset?.id;

    // If no transfers or no expected address, assume true (nothing to validate)
    if (!tokenTransfers || !expectedAddress) {
      return true;
    }

    const transactionChecks: TransactionChecks = {
      wasSentToExpectedReceiver: false,
      correctAsset: false,
      usedAsset: undefined,
      expectedAsset,
    };

    // Look for a matching transfer
    const matchedTransfer = tokenTransfers.find((transfer) => {
      const toAddress = transfer?.to?.toLowerCase();

      if (toAddress === expectedAddress) {
        transactionChecks.wasSentToExpectedReceiver = true;

        if (transfer.token === expectedAsset) {
          transactionChecks.correctAsset = true;
        } else {
          transactionChecks.usedAsset = transfer.token;
        }

        return true;
      }

      return false;
    });

    return (
      (transactionChecks.wasSentToExpectedReceiver && transactionChecks.correctAsset) ||
      (await this.modalForManualConfirmation(expectedAddress, tokenTransfers, transactionChecks))
    );
  }

  private async modalForManualConfirmation(
    receiverAddress: string,
    tokenTransfers: TokenTransfer[],
    transactionChecks: { wasSentToExpectedReceiver: boolean; correctAsset: boolean },
  ): Promise<boolean> {
    const response = await this.modalsService.openModal(ConfirmPaymentReportWithErrorModalComponent, {
      data: { tokenTransfers, receiverAddress, transactionChecks },
    });
    return response;
  }

  createPaymentReport(): CreatePaymentReportDto | null {
    const paymentDetails = this.paymentStore.paymentDetailsComputed();
    const profile = this.authStore.profile();
    const selectedReceiverWallet = paymentDetails.selectedReceiverWallet;
    const targetUser = selectedReceiverWallet?.user;

    const email = profile?.email || paymentDetails?.email;
    const name = profile?.name || paymentDetails?.payerName;

    if (!email || !name || !selectedReceiverWallet) {
      console.warn('Not all data to complete payment report.');
      return null;
    }

    const paymentTransactions = paymentDetails.transactionId
      ? [
          {
            blockchainTransactionId: formatCryptoAddressOrTx(
              paymentDetails.transactionId,
              this.paymentStore.paymentDetailsComputed().payingAsset?.assetChain || '',
            ),
            blockchainAssetId: selectedReceiverWallet.cryptoAddress.blockchainAsset.id || '',
            receiverAddress: selectedReceiverWallet.cryptoAddress.address || '',
            conversionQuoteId: paymentDetails.quote?.id || null,
            cryptoConnectionId: selectedReceiverWallet.cryptoAddress.cryptoConnectionId || null,
            cryptoAddressUsageLogId: selectedReceiverWallet.cryptoAddress.cryptoAddressUsageLogId || null,
          },
        ]
      : [];

    return {
      payerUserAnon: {
        email,
        name,
        publicName: paymentDetails.company || null,
      },
      existingBillShortId: this.paymentStore.paymentRequest?.()?.bill?.shortId || null,
      billDetails: targetUser
        ? {
            receiverUserId: targetUser.id || null,
            receiverUserAnon: null,
            invoiceReference: paymentDetails.reference || null,
            description: undefined,
          }
        : null,
      paymentTransactions: paymentTransactions.length > 0 ? paymentTransactions : null,
    };
  }

  async validatePayment(retryCount = 0): Promise<void> {
    const { paymentDetails, updateTxInfo } = this.paymentStore;
    const payingAsset = paymentDetails?.().payingAsset;
    const currentQuote = paymentDetails?.().quote;

    const userConfirmedPayment =
      this.successMetamaskTransaction ||
      (await this.modalsService.openModal(TransactionIdInputModalComponent, { data: this }));

    if (!userConfirmedPayment) return;

    const paymentReportObject = this.createPaymentReport();
    if (!paymentReportObject) return;

    try {
      const response = await lastValueFrom(
        this.paymentService.txInfo({
          paymentTransactions: paymentReportObject.paymentTransactions || [],
        }),
      );

      // Different address modal check COMMENT IF IT'S NOT WORKING.
      const isExpectedReceiver = await this.checkIfItsTheExpectedReceiver(response);

      if (!isExpectedReceiver) {
        this.validatePayment();
        return;
      }

      updateTxInfo(response[0]);
    } catch (error) {
      if (this.shouldRetry(error, retryCount)) {
        await this.retryWithDelay(() => this.validatePayment(retryCount + 1));
        return;
      }
      this.showInvalidTransactionError(payingAsset);
      return;
    }

    try {
      const response = await firstValueFrom(this.paymentService.doTransaction(paymentReportObject));
      this.clearTimer();
      if (response) this.wizardReportStore.setStep('success');
    } catch (error: any) {
      this.handleTransactionError(error, retryCount, payingAsset, currentQuote);
    }
  }

  private shouldRetry(error: any, retryCount: number): boolean {
    return (
      this.successMetamaskTransaction &&
      retryCount < 3 &&
      error.error?.detail?.toLowerCase().includes('invalid transaction id')
    );
  }

  private async retryWithDelay(callback: () => Promise<void>, delay = 1000): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, delay));
    await callback();
  }

  private showInvalidTransactionError(payingAsset: any): void {
    this.toastrFactory.error(
      this.translate.instant('thisIsNotAValidTransactionForTheNetwork', {
        asset: payingAsset?.assetTicker,
        network: formatNetworkName(payingAsset?.assetChain || ''),
      }),
    );
  }

  private handleTransactionError(error: any, retryCount: number, payingAsset: any, currentQuote: any): void {
    const errorDetail = error.error?.detail?.trim().toLowerCase() || '';

    if (this.shouldRetry(error, retryCount)) {
      this.successMetamaskTransaction = false;
      this.retryWithDelay(() => this.validatePayment(retryCount + 1));
      return;
    }

    if (errorDetail.includes('quote is no longer valid')) {
      this.showQuoteExpiredError(currentQuote);
      return;
    }

    this.toastrFactory.error(errorDetail || this.translate.instant('unknownError'), '', { timeOut: 3000 });
  }

  private showQuoteExpiredError(currentQuote: any): void {
    const now = new Date();
    const endTime = new Date(currentQuote?.validEndTime || '');

    this.toastrFactory.error(
      this.translate.instant('quoteExpired', {
        nowDate: now.toLocaleDateString('en-US'),
        timeNow: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        limitDate: endTime.toLocaleDateString('en-US'),
        limitTime: endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      }),
    );
  }

  fillTimeLapseBar(currentTime: number, totalTime: number) {
    if (totalTime === 0) {
      return `linear-gradient(90deg, rgba(120, 175, 0, 1) 0%, rgba(28, 28, 28, 0.1) 0%)`;
    }

    const percentage = (currentTime / totalTime) * 100;
    return `linear-gradient(90deg, rgba(120, 175, 0, 1) ${percentage}%, rgba(28, 28, 28, 0.1) ${percentage}%)`;
  }

  payingAssetSupportsBrowserExtension() {
    const payingAssetChain = this.paymentStore.paymentDetailsComputed().payingAsset?.assetChain;
    return payingAssetChain === 'ETH' || payingAssetChain === 'MATIC';
  }

  startTimer() {
    this.timerPercentage = 0;

    if (this.sameAssets()) return;

    this.clearTimer();

    this.getCurrencyInterval = setInterval('', minutes * 60000);
    this.timer = setInterval(() => {
      const currentTime = this.getCurrentTime();
      const timeDifference = this.endtime.getTime() - currentTime;
      const remainingTimeInSeconds = timeDifference / 1000;

      if (remainingTimeInSeconds <= 0) {
        this.refreshQuotesTimerInSeconds = 0;
        this.invalidQuote = true;
        this.wizardReportStore.setStep('outdated');
        this.clearTimer();
        return;
      }

      this.refreshQuotesTimerInSeconds = remainingTimeInSeconds;
    }, 1000);
  }

  getCurrentTime() {
    return Date.now();
  }

  public startPaymentWallet(selectedWallet: EIP6963ProviderDetail) {
    this.selectedWallet = selectedWallet;
    this.awaitingForWalletExtension.set(true);
    this.walletStore
      .handleWalletPayment(this.selectedWallet, this.paymentStore.paymentDetailsComputed())
      .then(({ transactionId, success }: { transactionId: string; success: boolean }) => {
        if (success) {
          this.paymentStore.updatePaymentDetails({ transactionId });
          this.successMetamaskTransaction = true;
          this.validatePayment();
        }
        this.awaitingForWalletExtension.set(false);
      })
      .catch((error: Error) => {
        console.error(error);
        this.awaitingForWalletExtension.set(false);
        this.handleWalletError(error);
      });
  }

  private handleWalletError(error: Error) {
    const errorDetail = error.message?.toLowerCase() || '';

    if (errorDetail.includes('rejected')) {
      this.toastrFactory.error(
        this.translate.instant('theTransactionHasBeenRejectedInMetamask', {
          walletExtension: this.selectedWallet.info.name,
        }),
      );
      return;
    }

    if (errorDetail.includes('transfer amount exceeds balance')) {
      this.toastrFactory.error(
        this.translate.instant('notEnoughFundsInMetaMask', {
          assetName: this.paymentStore.paymentDetailsComputed().payingAsset?.id,
          walletExtension: this.selectedWallet.info.name,
        }),
      );
      return;
    }

    this.toastrFactory.error(
      this.translate.instant('ensureMetaMaskNetworkConfigured', {
        networkName: formatNetworkName(this.paymentStore.paymentDetailsComputed().payingAsset?.assetChain || ''),
        walletExtension: this.selectedWallet.info.name,
      }),
    );
  }

  getSelectedWalletPng() {
    const walletName = this.selectedWallet.info.name.toLocaleLowerCase().replace(' ', '');
    return `/assets/icons/wallets/${walletName}.png`;
  }

  onBack() {
    this.paymentStore.updatePaymentDetails({ shouldKeepQuote: false });
    this.paymentStore.setIsResumePayment(false);
    this.wizardReportStore.setStep('information');
  }

  async ngOnInit(): Promise<void> {
    if (!this.paymentStore.paymentDetailsComputed().shouldKeepQuote) {
      this.getQuotes();
    } else {
      this.timerPercentage = 1;
      this.refreshQuotesTimerInSeconds = 0;
      this.setQuoteInfo(this.paymentStore.paymentDetailsComputed().quote);
    }

    // Update URL for resumed payments
    if (this.paymentStore.resumePayment()) {
      this.paymentStore.generateReturnLink();
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }
}
